/**
 * BlitPass.ts — ONLY pass that writes to canvas.getCurrentTexture().
 *
 * Reads the shared HDR RenderTarget.color and outputs ACES-tonemapped LDR
 * to the swapchain. If the canvas format is *-srgb, the pipeline relies on
 * implicit linear→sRGB conversion of the color attachment.
 */

import blitWgsl from "../shaders/blit.wgsl?raw";
import type { ManagedRenderTarget } from "./RenderTarget";
import type { FrameContext, RenderPass } from "./RenderPass";

export class BlitPass implements RenderPass {
  readonly name = "BlitPass";
  private pipeline: GPURenderPipeline;
  private sampler: GPUSampler;
  private bindGroup: GPUBindGroup;
  private bindGroupLayout: GPUBindGroupLayout;
  private unsubscribe: () => void;
  private deviceRef: GPUDevice;

  constructor(
    device: GPUDevice,
    private rt: ManagedRenderTarget,
    swapchainFormat: GPUTextureFormat,
    private aoView: GPUTextureView,
  ) {
    this.deviceRef = device;
    const module = device.createShaderModule({
      label: "blit.wgsl",
      code: blitWgsl,
    });
    this.bindGroupLayout = device.createBindGroupLayout({
      label: "BlitPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: "filtering" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: "float" },
        },
      ],
    });
    this.pipeline = device.createRenderPipeline({
      label: "BlitPass.pipeline",
      layout: device.createPipelineLayout({
        bindGroupLayouts: [this.bindGroupLayout],
      }),
      vertex: { module, entryPoint: "vs_main" },
      fragment: {
        module,
        entryPoint: "fs_main",
        targets: [{ format: swapchainFormat }],
      },
      primitive: { topology: "triangle-list" },
    });
    this.sampler = device.createSampler({
      magFilter: "linear",
      minFilter: "linear",
    });

    this.bindGroup = this.makeBindGroup(device);
    this.unsubscribe = rt.onRecreate(() => {
      this.bindGroup = this.makeBindGroup(device);
    });
  }

  private makeBindGroup(device: GPUDevice): GPUBindGroup {
    return device.createBindGroup({
      label: "BlitPass.bg",
      layout: this.bindGroupLayout,
      entries: [
        { binding: 0, resource: this.rt.current.colorView },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: this.aoView },
      ],
    });
  }

  /** Replace the AO source view (after RT resize). */
  setAoView(view: GPUTextureView): void {
    this.aoView = view;
    this.bindGroup = this.makeBindGroup(this.deviceRef);
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    const pass = encoder.beginRenderPass({
      label: "BlitPass",
      colorAttachments: [
        {
          view: frame.swapchainView,
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3, 1, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.unsubscribe();
  }
}
